import React, {useCallback, useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, Flex} from '@salesforce/retail-react-app/app/components/shared/ui'
import Swatch from '../../swatch-group/swatch'
import {filterImageGroups, getDecoratedVariationAttributes} from '../../../utils/product-utils'

const ProductTileDetailsColorSwatches = ({
    product,
    styles,
    colorValue,
    setColorValue,
    setProductId,
    productId
}) => {
    const sourceProduct = useMemo(() => {
        if (product?.variationAttributes?.length && product?.imageGroups?.length) {
            return product
        }
        if (product?.representedProduct?.variationAttributes?.length) {
            return product.representedProduct
        }
        return product
    }, [product])

    const decoratedVariationAttributes = useMemo(
        () => getDecoratedVariationAttributes(sourceProduct) || [],
        [sourceProduct]
    )

    const colorAttribute = useMemo(() => {
        const decoratedColor = decoratedVariationAttributes.find(({id}) => id === 'color')
        if (decoratedColor) return decoratedColor
        return sourceProduct?.variationAttributes?.find(({id}) => id === 'color')
    }, [decoratedVariationAttributes, sourceProduct?.variationAttributes])

    const values = colorAttribute?.values || []

    const derivedValues = useMemo(() => {
        if (values.length) return values

        const variationGroups =
            product?.variationGroups ||
            product?.variants ||
            sourceProduct?.variationGroups ||
            sourceProduct?.variants ||
            []

        const colorValues = variationGroups
            .map((group) => group?.variationValues?.color)
            .filter(Boolean)

        const uniqueColorValues = Array.from(new Set(colorValues))

        if (!uniqueColorValues.length) return []

        return uniqueColorValues.map((value) => {
            const swatchImage = filterImageGroups(sourceProduct?.imageGroups, {
                viewType: 'swatch',
                variationValues: {color: value}
            })?.[0]?.images?.[0]

            return {
                value,
                name: value,
                swatch: swatchImage
            }
        })
    }, [values, product?.variationGroups, product?.variants, sourceProduct])

    const selectedColorFromProductId = useMemo(() => {
        if (!productId) return null
        const variationGroups =
            product?.variationGroups ||
            product?.variants ||
            sourceProduct?.variationGroups ||
            sourceProduct?.variants ||
            []
        const matchedGroup = variationGroups.find((group) => group?.productId === productId)
        return matchedGroup?.variationValues?.color || null
    }, [productId, product, sourceProduct])

    const selectedColor =
        colorValue ||
        selectedColorFromProductId ||
        sourceProduct?.variationValues?.color ||
        product?.variationValues?.color ||
        sourceProduct?.c_color ||
        product?.c_color ||
        null

    const handleColorChange = useCallback(
        (value) => {
            if (!value) return

            const variationGroups =
                product?.variationGroups ||
                product?.variants ||
                sourceProduct?.variationGroups ||
                sourceProduct?.variants ||
                []
            const matchedGroup = variationGroups.find(
                (group) => group?.variationValues?.color === value
            )

            if (matchedGroup?.productId) {
                setProductId?.(matchedGroup.productId)
            }

            if (setColorValue) {
                setColorValue(value)
            }
        },
        [product?.variationGroups, product?.variants, setProductId, setColorValue]
    )

    const swatchValues = (values.length ? values : derivedValues).slice(0, 5)

    if (!swatchValues.length) {
        return null
    }

    return (
        <Box
            {...styles.colorSwatchCarouselContainer}
            onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
            }}
            onMouseDown={(event) => {
                event.preventDefault()
                event.stopPropagation()
            }}
        >
            <Flex {...styles.colorSwatchRow} role="radiogroup" aria-label="Colors">
                {swatchValues.map((value) => {
                    if (value?.online === false) {
                        return null
                    }

                    const swatchImage = value?.image || value?.swatch
                    const swatchImageUrl = swatchImage?.disBaseLink || swatchImage?.link
                    const isSelected = selectedColor === value?.value

                    const content = swatchImageUrl ? (
                        <Box
                            {...styles.colorSwatchImage}
                            backgroundColor={
                                typeof value?.name === 'string'
                                    ? value.name.toLowerCase()
                                    : undefined
                            }
                            backgroundImage={`url(${swatchImageUrl})`}
                        />
                    ) : (
                        <Box {...styles.colorSwatchText}>{value?.name}</Box>
                    )

                    return (
                        <Swatch
                            key={value?.value}
                            value={value?.value}
                            name={value?.name}
                            isProductTile
                            variant="circle"
                            selected={isSelected}
                            disabled={value?.orderable === false}
                            isFocusable={true}
                            handleSelect={handleColorChange}
                            {...styles.colorSwatchItem}
                        >
                            {content}
                        </Swatch>
                    )
                })}
            </Flex>
        </Box>
    )
}

ProductTileDetailsColorSwatches.propTypes = {
    product: PropTypes.object,
    styles: PropTypes.object,
    colorValue: PropTypes.string,
    setColorValue: PropTypes.func,
    setProductId: PropTypes.func,
    productId: PropTypes.string
}

export default ProductTileDetailsColorSwatches
